import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, AppBar, Toolbar, IconButton, Typography, Button, Menu, MenuItem } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useLocation, useNavigate } from 'react-router-dom';
import SideNav from './side-nav';
import { getStoredUser } from '../auth';
import InternalOrderNotifier from './internal-order-notifier';

const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;

const Layout = ({ children }) => {
  const [sideNavOpen, setSideNavOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [currentUser, setCurrentUser] = useState(getStoredUser());
  const inactivityTimerRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const toggleSideNav = () => {
    setSideNavOpen((prev) => !prev);
  };

  useEffect(() => {
    setCurrentUser(getStoredUser());
  }, [location.pathname]);

  const userDisplay = useMemo(() => {
    if (!currentUser) return '';
    const companyName = currentUser.customer?.customerName || 'Company';
    const fullName = `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim();
    return `${companyName} - ${fullName}`;
  }, [currentUser]);

  const handleOpenUserMenu = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setUserMenuAnchor(null);
  };

  const handleUpdateProfile = () => {
    handleCloseUserMenu();
    navigate('/profile');
  };

  const performLogout = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setCurrentUser(null);
    setUserMenuAnchor(null);
    navigate('/login', { replace: true });
  }, [navigate]);

  const handleLogout = () => {
    handleCloseUserMenu();
    performLogout();
  };

  useEffect(() => {
    if (!currentUser) return undefined;

    const resetInactivityTimer = () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = setTimeout(() => {
        performLogout();
      }, INACTIVITY_TIMEOUT_MS);
    };

    const activityEvents = ['click', 'keydown', 'mousemove', 'scroll', 'touchstart'];
    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, resetInactivityTimer, { passive: true });
    });

    resetInactivityTimer();

    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, resetInactivityTimer);
      });
    };
  }, [currentUser, performLogout]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <SideNav open={sideNavOpen} onClose={toggleSideNav} />
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {currentUser && location.pathname !== '/login' ? <InternalOrderNotifier /> : null}
        <AppBar
          position="static"
          elevation={0}
          sx={{
            background: 'linear-gradient(90deg, #0d2538 0%, #112f47 55%, #cec8bf 100%)',
            borderBottom: '1px solid rgba(255,255,255,0.18)',
          }}
        >
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={toggleSideNav}>
              <MenuIcon />
            </IconButton>
            <Box
              component="img"
              src="/favicon.ico"
              alt="TRS"
              sx={{ width: 32, height: 32, borderRadius: 1, mr: 1 }}
            />
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              TRS - Transload Portal
            </Typography>
            {currentUser ? (
              <>
                <Button color="inherit" endIcon={<ArrowDropDownIcon />} onClick={handleOpenUserMenu}>
                  {userDisplay}
                </Button>
                <Menu anchorEl={userMenuAnchor} open={Boolean(userMenuAnchor)} onClose={handleCloseUserMenu}>
                  <MenuItem onClick={handleUpdateProfile}>Update Profile</MenuItem>
                  <MenuItem onClick={handleLogout}>Logout</MenuItem>
                </Menu>
              </>
            ) : (
              <Button color="inherit" onClick={() => navigate('/login')}>
                Login
              </Button>
            )}
          </Toolbar>
        </AppBar>

        <Box sx={{ p: 2, flexGrow: 1 }} className="page-enter">
          {children}
        </Box>

        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderTop: '1px solid var(--ui-border)',
            backgroundColor: 'var(--ui-surface-soft)',
          }}
        >
          <Typography variant="caption" sx={{ color: 'var(--ui-muted)', textAlign: 'center', display: 'block' }}>
            Copyright {"\u00A9"} {new Date().getFullYear()} Tennessee Rail Systems, LLC. All rights reserved.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;



